import { isDifficultyTier, type DifficultyTier } from '../config/difficulty';
import { ALL_SURFACE_IDS, type SurfaceId } from './Surface';

export interface LevelSurfaceDefinition {
  enabled: boolean;
  magnetic: boolean;
}

/** One rectangular zone of a multi-zone level. */
export interface LevelZoneDefinition {
  id: string;
  /** World position of the zone's floor centre. */
  origin: [number, number, number];
  /** Zone size in metres: width (x), height (y), depth (z). */
  size: [number, number, number];
  /** Per-zone surface overrides; the level's `surfaces` are the default. */
  surfaces?: Record<SurfaceId, LevelSurfaceDefinition>;
  /** Faces of this zone that are exterior hull, in the zone's local frame. */
  hullFaces?: SurfaceId[];
}

/** A doorway joining two zones, authored in world space. */
export interface LevelPortalDefinition {
  id: string;
  a: string;
  b: string;
  axis: 'x' | 'z';
  position: [number, number, number];
  width: number;
  height: number;
}

/** Optional visual styling for a level. */
export interface LevelStyleDefinition {
  palette?: string;
  wallColor?: number;
  floorColor?: number;
  ceilingColor?: number;
  pathColor?: number;
  path?: Array<{
    from: [number, number];
    to: [number, number];
    width?: number;
  }>;
  ceilingLights?: Array<{
    position: [number, number, number];
    size: [number, number, number];
  }>;
}

export interface LevelDefinition {
  id: string;
  name: string;
  /** Chamber size in metres: width (x), height (y), depth (z). */
  size: [number, number, number];
  surfaces: Record<SurfaceId, LevelSurfaceDefinition>;
  /** Optional zones. When present, the level is a connected multi-zone map. */
  zones?: LevelZoneDefinition[];
  /** Doorways between zones. */
  portals?: LevelPortalDefinition[];
  /**
   * How hard the drones on this deck hit back. Omitted, the level runs at
   * `standard`; see `config/difficulty.ts` for what each tier changes.
   */
  difficulty?: DifficultyTier;
  /** Optional visual style. */
  style?: LevelStyleDefinition;
  spawn: {
    zone?: string;
    surface: SurfaceId;
    position: [number, number, number];
    facing?: number;
  };
  targets: Array<{
    type: 'drone';
    zone?: string;
    surface: SurfaceId;
    position: [number, number, number];
    /** Magnetic drones can be pulled by the grapple hook. */
    magnetic?: boolean;
    /** A looping patrol route in world metres; two or more points. */
    route?: Array<[number, number, number]>;
  }>;
  obstacles: Array<{
    type: 'box';
    kind?: string;
    zone?: string;
    position: [number, number, number];
    size: [number, number, number];
    magnetic?: boolean;
  }>;
  seed: number;
}

export interface ValidationResult {
  ok: boolean;
  errors: string[];
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Validate a level definition. The result names the offending field so a bad
 * level file can be fixed without guesswork.
 */
export function validateLevel(input: unknown): ValidationResult {
  const errors: string[] = [];

  if (!isRecord(input)) return { ok: false, errors: ['level: expected an object'] };

  if (typeof input.id !== 'string' || input.id.length === 0)
    errors.push('id: expected a non-empty string');
  if (typeof input.name !== 'string') errors.push('name: expected a string');

  if (!isSizeTuple(input.size)) {
    errors.push('size: expected [width, height, depth] as positive numbers');
  } else if (input.size.some((value) => value <= 0)) {
    errors.push('size: width, height and depth must be greater than zero');
  }

  if (!isRecord(input.surfaces)) {
    errors.push('surfaces: expected an object keyed by surface id');
  } else {
    for (const id of ALL_SURFACE_IDS) {
      const entry = input.surfaces[id];
      if (!isRecord(entry)) {
        errors.push(`surfaces.${id}: expected { enabled, magnetic }`);
        continue;
      }
      if (typeof entry.enabled !== 'boolean')
        errors.push(`surfaces.${id}.enabled: expected a boolean`);
      if (typeof entry.magnetic !== 'boolean')
        errors.push(`surfaces.${id}.magnetic: expected a boolean`);
    }
  }

  if (input.zones !== undefined) {
    if (!Array.isArray(input.zones)) {
      errors.push('zones: expected an array');
    } else {
      input.zones.forEach((zone, index) => {
        if (!isRecord(zone)) {
          errors.push(`zones[${index}]: expected an object`);
          return;
        }
        if (typeof zone.id !== 'string' || zone.id.length === 0)
          errors.push(`zones[${index}].id: expected a non-empty string`);
        if (!isVec3(zone.origin)) errors.push(`zones[${index}].origin: expected [x, y, z]`);
        if (!isVec3(zone.size)) {
          errors.push(`zones[${index}].size: expected [x, y, z]`);
        } else if (zone.size.some((value) => value <= 0)) {
          errors.push(`zones[${index}].size: width, height and depth must be greater than zero`);
        }
        if (zone.hullFaces !== undefined) {
          if (!Array.isArray(zone.hullFaces)) {
            errors.push(`zones[${index}].hullFaces: expected an array`);
          } else if (!zone.hullFaces.every(isSurfaceId)) {
            errors.push(`zones[${index}].hullFaces: unknown surface id`);
          }
        }
      });
    }
  }

  const zoneIds = Array.isArray(input.zones)
    ? input.zones
        .filter(isRecord)
        .map((zone) => zone.id)
        .filter((id): id is string => typeof id === 'string')
    : null;

  if (input.portals !== undefined) {
    if (!Array.isArray(input.portals)) {
      errors.push('portals: expected an array');
    } else {
      input.portals.forEach((portal, index) => {
        if (!isRecord(portal)) {
          errors.push(`portals[${index}]: expected an object`);
          return;
        }
        if (typeof portal.id !== 'string' || portal.id.length === 0)
          errors.push(`portals[${index}].id: expected a non-empty string`);
        if (typeof portal.a !== 'string') errors.push(`portals[${index}].a: expected a zone id`);
        if (typeof portal.b !== 'string') errors.push(`portals[${index}].b: expected a zone id`);
        if (zoneIds && typeof portal.a === 'string' && !zoneIds.includes(portal.a))
          errors.push(`portals[${index}].a: unknown zone id`);
        if (zoneIds && typeof portal.b === 'string' && !zoneIds.includes(portal.b))
          errors.push(`portals[${index}].b: unknown zone id`);
        if (portal.axis !== 'x' && portal.axis !== 'z')
          errors.push(`portals[${index}].axis: expected "x" or "z"`);
        if (!isVec3(portal.position)) errors.push(`portals[${index}].position: expected [x, y, z]`);
        if (!isFiniteNumber(portal.width) || portal.width <= 0)
          errors.push(`portals[${index}].width: expected a positive number`);
        if (!isFiniteNumber(portal.height) || portal.height <= 0)
          errors.push(`portals[${index}].height: expected a positive number`);
      });
    }
  }

  if (input.difficulty !== undefined && !isDifficultyTier(input.difficulty)) {
    errors.push('difficulty: expected calm, standard, hard or lethal');
  }

  if (!isRecord(input.spawn)) {
    errors.push('spawn: expected an object');
  } else {
    if (!isSurfaceId(input.spawn.surface)) errors.push('spawn.surface: unknown surface id');
    if (!isVec3(input.spawn.position)) errors.push('spawn.position: expected [x, y, z]');
    if (input.spawn.facing !== undefined && !isFiniteNumber(input.spawn.facing)) {
      errors.push('spawn.facing: expected a number');
    }
  }

  if (!Array.isArray(input.targets)) {
    errors.push('targets: expected an array');
  } else {
    input.targets.forEach((target, index) => {
      if (!isRecord(target)) {
        errors.push(`targets[${index}]: expected an object`);
        return;
      }
      if (target.type !== 'drone') errors.push(`targets[${index}].type: only "drone" is supported`);
      if (!isSurfaceId(target.surface))
        errors.push(`targets[${index}].surface: unknown surface id`);
      if (!isVec3(target.position)) errors.push(`targets[${index}].position: expected [x, y, z]`);
      if (target.magnetic !== undefined && typeof target.magnetic !== 'boolean') {
        errors.push(`targets[${index}].magnetic: expected a boolean`);
      }
      if (target.route !== undefined) {
        if (!Array.isArray(target.route)) {
          errors.push(`targets[${index}].route: expected an array of [x, y, z] points`);
        } else if (target.route.length > 0 && target.route.length < 2) {
          errors.push(`targets[${index}].route: expected at least two points`);
        } else if (!target.route.every((point) => isVec3(point))) {
          errors.push(`targets[${index}].route: expected an array of [x, y, z] points`);
        }
      }
    });
  }

  if (!Array.isArray(input.obstacles)) {
    errors.push('obstacles: expected an array');
  } else {
    input.obstacles.forEach((obstacle, index) => {
      if (!isRecord(obstacle)) {
        errors.push(`obstacles[${index}]: expected an object`);
        return;
      }
      if (obstacle.type !== 'box') errors.push(`obstacles[${index}].type: only "box" is supported`);
      if (!isVec3(obstacle.position))
        errors.push(`obstacles[${index}].position: expected [x, y, z]`);
      if (!isVec3(obstacle.size)) errors.push(`obstacles[${index}].size: expected [x, y, z]`);
    });
  }

  if (!isFiniteNumber(input.seed)) errors.push('seed: expected a number');

  return { ok: errors.length === 0, errors };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isVec3(value: unknown): value is [number, number, number] {
  return Array.isArray(value) && value.length === 3 && value.every(isFiniteNumber);
}

function isSizeTuple(value: unknown): value is [number, number, number] {
  return isVec3(value);
}

function isSurfaceId(value: unknown): value is SurfaceId {
  return typeof value === 'string' && (ALL_SURFACE_IDS as string[]).includes(value);
}
